import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Custom gallery component for ELO product pages.
 * Uses Swiper for smooth looping carousel.
 * Layout: one large image on top + 5 thumbnails with arrows below.
 * Both sliders are linked via Swiper controller.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} topSwiper - Top swiper container
 * @property {HTMLElement} thumbSwiper - Thumbs swiper container
 * @property {HTMLElement} [mobileProgressBar] - Mobile slideshow progress bar inner fill
 *
 * @extends Component<Refs>
 */
export class EloGallery extends Component {
  /** @type {any} */
  #topSwiperInstance = null;

  /** @type {any} */
  #thumbSwiperInstance = null;

  /** @type {boolean} */
  #syncing = false;

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.#handleVariantUpdate), { signal });

    // Handle zoom clicks via delegation (works for Swiper loop clones too)
    this.#initZoomClickHandler(signal);

    // Update mobile progress bar on slideshow slide change
    this.addEventListener('slideshow:select', /** @type {EventListener} */ (this.#handleMobileSlideSelect), { signal });

    // Swiper may not be loaded yet (deferred script), wait for it
    if (/** @type {any} */ (window).Swiper) {
      requestAnimationFrame(() => this.#initSwipers());
    } else {
      const script = document.querySelector('script[src*="swiper-bundle"]');
      if (script) {
        script.addEventListener('load', () => requestAnimationFrame(() => this.#initSwipers()), { signal });
      }
      // Fallback: if we missed the load event, try on DOMContentLoaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          if (!this.#topSwiperInstance && /** @type {any} */ (window).Swiper) {
            requestAnimationFrame(() => this.#initSwipers());
          }
        }, { signal });
      } else if (!this.#topSwiperInstance) {
        // Document already loaded, try init in next frame
        requestAnimationFrame(() => {
          if (!this.#topSwiperInstance && /** @type {any} */ (window).Swiper) {
            this.#initSwipers();
          }
        });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#topSwiperInstance?.destroy(true, true);
    this.#thumbSwiperInstance?.destroy(true, true);
  }

  #initZoomClickHandler(signal) {
    const zoomDialogId = this.dataset.zoomDialogId;
    if (!zoomDialogId) return;

    const desktopEl = this.querySelector('.elo-gallery__desktop');
    if (!desktopEl) return;

    desktopEl.addEventListener('click', (event) => {
      const slide = /** @type {HTMLElement} */ (event.target)?.closest('.product-media-container--zoomable');
      if (!slide) return;

      const mediaIndex = slide.getAttribute('data-media-index');
      if (mediaIndex == null) return;

      const zoomDialog = /** @type {any} */ (document.getElementById(zoomDialogId));
      if (!zoomDialog?.open) return;

      zoomDialog.open(parseInt(mediaIndex, 10), event);
    }, { signal });
  }

  #initSwipers() {
    // @ts-ignore — Swiper loaded as global from swiper-bundle.min.js
    const SwiperClass = /** @type {any} */ (window).Swiper;
    if (!SwiperClass) return;

    this.#initDesktopSwipers(SwiperClass);
  }

  #initDesktopSwipers(SwiperClass) {
    const topEl = this.refs.topSwiper;
    const thumbEl = this.refs.thumbSwiper;
    if (!topEl || !thumbEl) return;

    const gap = parseInt(getComputedStyle(this).getPropertyValue('--image-gap')) || 8;
    const desktopGap = 18;
    const thumbsPerView = 5;

    // Swiper's loop mode needs more slides than fit in one view to create
    // its clone slides correctly. With few images (e.g. exactly 3 thumbs
    // for slidesPerView: 3), looping breaks: arrows stop responding or the
    // sliders desync. Only enable loop once there are enough slides to
    // actually loop through — otherwise all slides already fit in view, so
    // looping/navigation isn't needed anyway.
    const topSlideCount = topEl.querySelectorAll('.swiper-slide').length;
    const thumbSlideCount = thumbEl.querySelectorAll('.swiper-slide').length;
    const topLoop = topSlideCount > 1;
    const thumbLoop = thumbSlideCount > thumbsPerView;

    this.#topSwiperInstance = new SwiperClass(topEl, {
      slidesPerView: 1,
      spaceBetween: gap,
      loop: topLoop,
      loopedSlides: 1,
      allowTouchMove: false,
    });

    this.#thumbSwiperInstance = new SwiperClass(thumbEl, {
      slidesPerView: thumbsPerView,
      spaceBetween: desktopGap,
      loop: thumbLoop,
      loopedSlides: 1,
      allowTouchMove: false,
      navigation: {
        nextEl: this.querySelector('.elo-gallery__arrow--next'),
        prevEl: this.querySelector('.elo-gallery__arrow--prev'),
      },
    });

    // A thumbnail is a direct way to select its corresponding main image.
    // Move both sliders with Swiper's built-in animation so the selected
    // thumbnail remains in view and the main image changes smoothly.
    thumbEl.addEventListener('click', (event) => {
      const thumb = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-media-index]');
      if (!thumb || !thumbEl.contains(thumb)) return;

      event.preventDefault();
      event.stopPropagation();

      const mediaIndex = Number(thumb.dataset.mediaIndex);
      if (!Number.isInteger(mediaIndex)) return;

      this.#topSwiperInstance?.slideToLoop(mediaIndex);

      const thumbIndex = Array.from(thumbEl.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')).indexOf(thumb);
      if (thumbIndex >= 0) {
        this.#thumbSwiperInstance?.slideToLoop(thumbIndex);
      }
    });

    // Manually sync: when thumbs move, move top slider the same direction.
    // This avoids Swiper controller.control desync with different slidesPerView + loop.
    this.#thumbSwiperInstance.on('slideNextTransitionStart', () => {
      if (this.#syncing) return;
      this.#syncing = true;
      this.#topSwiperInstance?.slideNext(this.#thumbSwiperInstance.params.speed);
      this.#syncing = false;
    });

    this.#thumbSwiperInstance.on('slidePrevTransitionStart', () => {
      if (this.#syncing) return;
      this.#syncing = true;
      this.#topSwiperInstance?.slidePrev(this.#thumbSwiperInstance.params.speed);
      this.#syncing = false;
    });
  }

  /**
   * Updates the mobile progress bar width based on the current slide index.
   * Also scrolls the mobile thumbnail strip so the active thumb stays visible.
   * @param {CustomEvent<{ index: number }>} event
   */
  #handleMobileSlideSelect = (event) => {
    const bar = /** @type {HTMLElement|undefined} */ (this.refs?.mobileProgressBar);
    if (!bar) return;
    const total = this.querySelectorAll('.elo-gallery__mobile-slideshow .slideshow-slide').length
      || this.querySelectorAll('.elo-gallery__mobile slideshow-slide').length;
    if (!total) return;
    const index = Math.max(0, Math.min(total - 1, Number(event.detail?.index) || 0));
    bar.style.setProperty('--mobile-progress', `${((index + 1) / total) * 100}%`);

    // Scroll the thumbnail strip only when the active thumbnail is outside the visible area
    const mobileEl = this.querySelector('.elo-gallery__mobile');
    if (!mobileEl) return;
    const thumbs = mobileEl.querySelectorAll('.slideshow-controls__thumbnail');
    const activeThumb = /** @type {HTMLElement|null} */ (thumbs[index] ?? null);
    if (!activeThumb) return;
    const scrollContainer = /** @type {HTMLElement|null} */ (activeThumb.closest('.slideshow-controls__thumbnails-container'));
    if (!scrollContainer) return;
    const containerRect = scrollContainer.getBoundingClientRect();
    const thumbRect = activeThumb.getBoundingClientRect();
    const isVisible = thumbRect.left >= containerRect.left && thumbRect.right <= containerRect.right;
    if (isVisible) return;
    const thumbCenter = thumbRect.left + thumbRect.width / 2;
    const containerCenter = containerRect.left + containerRect.width / 2;
    scrollContainer.scrollBy({ left: thumbCenter - containerCenter, behavior: 'smooth' });
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newGallery = source.querySelector('elo-gallery');
    if (!newGallery) return;

    // Compare media IDs — if the images are the same (e.g. size change), skip replacement to preserve Swiper state
    const currentMediaIds = this.#getMediaIds(this);
    const newMediaIds = this.#getMediaIds(newGallery);

    if (currentMediaIds === newMediaIds) return;

    this.replaceWith(newGallery);
  };

  /**
   * Extracts a string of media IDs from gallery for comparison.
   * @param {HTMLElement} gallery
   * @returns {string}
   */
  #getMediaIds(gallery) {
    const mediaElements = gallery.querySelectorAll('.elo-gallery__desktop [data-media-id]');
    return Array.from(mediaElements)
      .map((el) => el.getAttribute('data-media-id'))
      .join(',');
  }
}

if (!customElements.get('elo-gallery')) {
  customElements.define('elo-gallery', EloGallery);
}
